import { useCallback, useMemo } from 'react';

import { sendOddsClickCustomEvent } from '@cx-modules/utils';

import { useSelector } from '../providers/StoreProvider';
import { type Bet, type Odd } from '../types';

/**
 * Optimized version of useBetslip that accepts bet data as props
 * to avoid calling useBets() internally (preventing duplicate hook calls)
 */
export const useBetslipOptimized = ({
  bet,
  betId,
  bookieCode,
  odds,
  oddsText,
}: {
  bet: Bet | undefined;
  betId: number;
  bookieCode: string;
  odds: Odd | undefined;
  oddsText?: string | number;
}): { isBookieBetSelected: boolean; toggleBetslipBet: () => void } => {
  const { addSelectedBet, marketName, removeSelectedBet, selectedBets } = useSelector(store => ({
    addSelectedBet: store.addSelectedBet,
    marketName: store.marketName,
    removeSelectedBet: store.removeSelectedBet,
    selectedBets: store.selectedBets,
  }));

  const isBookieBetSelected = useMemo(
    () => selectedBets.some(selectedBet => selectedBet.betId === betId && selectedBet.bookieCode === bookieCode),
    [betId, selectedBets, bookieCode],
  );

  const bookieBet = useMemo(() => odds || ({} as Odd), [odds]);

  const removeFromBetslip = useCallback(() => {
    removeSelectedBet(betId);

    sendOddsClickCustomEvent({
      betLocation: 'odds-grid',
      betName: `${bet?.name} ${bet?.line?.name}`,
      betOdds: [bookieBet],
      bookieCode,
      event: 'remove_from_betslip',
      marketName,
      pageTitle: 'subevent-odds-grid',
    });
  }, [removeSelectedBet, betId, bet?.name, bet?.line?.name, bookieBet, bookieCode, marketName]);

  const addToBetslip = useCallback(() => {
    const existingBet = selectedBets.find(selectedBet => selectedBet.betId === betId);
    if (existingBet) {
      sendOddsClickCustomEvent({
        betLocation: 'odds-grid',
        betName: `${bet?.name} ${bet?.line?.name}`,
        betOdds: [bookieBet],
        bookieCode,
        event: 'odds_button_click_remove',
        marketName,
        pageTitle: 'subevent-odds-grid',
      });
    }

    addSelectedBet({ betId, bookieCode });

    sendOddsClickCustomEvent({
      betLocation: 'odds-grid',
      betName: `${bet?.name} ${bet?.line?.name}`,
      betOdds: [bookieBet],
      bookieCode,
      event: 'odds_button_click',
      marketName,
      pageTitle: 'subevent-odds-grid',
    });
  }, [selectedBets, addSelectedBet, betId, bookieCode, bet?.name, bet?.line?.name, bookieBet, marketName]);

  const toggleBetslipBet = useCallback(() => {
    try {
      window.bettingActions.addOrRemoveFromBetslip(
        [{ betId, selectedBookie: bookieCode }],
        [betId],
        bookieCode,
        oddsText?.toString(),
        `${bet?.name} ${bet?.line?.name}`,
        'odds-grid',
        'n/a',
        marketName,
        'n/a',
      );
    } catch (e) {
      console.error('useBetslip.toggleBetslipBet', e);
    }

    isBookieBetSelected ? removeFromBetslip() : addToBetslip();
  }, [
    isBookieBetSelected,
    removeFromBetslip,
    addToBetslip,
    betId,
    bookieCode,
    oddsText,
    bet?.name,
    bet?.line?.name,
    marketName,
  ]);

  return { isBookieBetSelected, toggleBetslipBet };
};
